import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Megaphone, Trophy, Gift, X, Clock } from 'lucide-react';
import { API_URL } from '../../utils/apiUrl';
import { clubhouseApi } from '../../utils/clubhouseApi';
import { logger } from '../../utils/logger';
import './NotificationBell.css';

// Get WebSocket URL from API URL
const getWebSocketUrl = () => {
  const apiUrl = API_URL || '';
  // Convert http(s) to ws(s)
  return apiUrl.replace(/^http/, 'ws');
};

const ICON_MAP = {
  megaphone: Megaphone,
  trophy: Trophy,
  gift: Gift,
  bell: Bell,
  clock: Clock
};

export const NotificationBell = ({ onModalReopen }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    // Only attempt WS when we have a session hint (cached user). The auth
    // cookie itself is httpOnly and not readable from JS, but the browser
    // will send it automatically on the same-origin WebSocket handshake.
    if (!localStorage.getItem('clubhouse_user')) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = `${getWebSocketUrl()}/api/clubhouse/notifications/ws`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        logger.debug('NotificationBell', 'WebSocket connected');
        setWsConnected(true);
        
        // Setup ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          // Handle pong response
          if (event.data === 'pong' || event.data === 'ping') {
            return;
          }

          const data = JSON.parse(event.data);
          
          if (data.type === 'unread_count') {
            setUnreadCount(data.count);
          } else if (data.type === 'new_notification') {
            // Add new notification to the top of the list
            setNotifications(prev => [data.notification, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Optional: Play notification sound or show browser notification
            // playNotificationSound();
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      ws.onclose = (event) => {
        logger.debug('NotificationBell', 'WebSocket closed', event.code, event.reason);
        setWsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Reconnect after 5 seconds if not a normal close
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            logger.debug('NotificationBell', 'attempting to reconnect WebSocket');
            connectWebSocket();
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [connectWebSocket]);

  // Fallback: fetch unread count if WebSocket not connected
  useEffect(() => {
    if (!wsConnected) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [wsConnected]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const fetchUnreadCount = async () => {
    try {
      if (!localStorage.getItem('clubhouse_user')) return;

      const { data } = await clubhouseApi.get('/api/clubhouse/notifications/unread-count');
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      // Fetch both notifications and active modals
      const [notificationsRes, activeModalsRes] = await Promise.all([
        clubhouseApi.get('/api/clubhouse/notifications?limit=20'),
        clubhouseApi.get('/api/clubhouse/modals/active'),
      ]);

      const notificationsData = notificationsRes.data || [];
      const activeModalsData = activeModalsRes.data || [];

      // Convert active modals to notification format
      const activeModalNotifications = activeModalsData.map(modal => ({
        id: `modal-${modal.id}`,
        user_id: 'current_user',
        type: 'modal',
        title: modal.title,
        content: {
          modal_id: modal.id,
          headline: modal.headline,
          body_text: modal.body_text,
          icon: modal.icon,
          full_modal: modal // Store full modal data for reopening
        },
        is_read: false,
        created_at: modal.created_at
      }));

      // Combine and sort by date (active modals first, then notifications)
      const combined = [...activeModalNotifications, ...notificationsData];
      setNotifications(combined);

      // Update unread count to include active modals
      const totalUnread = activeModalNotifications.length + notificationsData.filter(n => !n.is_read).length;
      setUnreadCount(totalUnread);

      // Mark notifications as seen when dropdown opens
      markAsSeen();

    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsSeen = async () => {
    try {
      await clubhouseApi.post('/api/clubhouse/notifications/mark-seen');
    } catch (error) {
      console.error('Error marking notifications as seen:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await clubhouseApi.post('/api/clubhouse/notifications/mark-all-read');
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await clubhouseApi.post(`/api/clubhouse/notifications/${notificationId}/read`);

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.type === 'modal' && onModalReopen) {
      // If it's an active modal (has full_modal data), pass that
      // Otherwise reconstruct from notification content
      const modalData = notification.content.full_modal || {
        id: notification.content.modal_id,
        title: notification.title,
        headline: notification.content.headline,
        body_text: notification.content.body_text,
        icon: notification.content.icon
      };
      onModalReopen(modalData);
      setIsOpen(false);
    }

    // Mark notification as read (including active modals)
    if (!notification.is_read) {
      // For active modals (temporary IDs), just update local state
      if (notification.id.startsWith('modal-')) {
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        // For real notifications, mark as read in database
        markAsRead(notification.id);
      }
    }
  };

  const getNotificationIcon = (notification) => {
    if (notification.type === 'modal') {
      const iconName = notification.content?.icon || 'megaphone';
      const IconComponent = ICON_MAP[iconName] || Megaphone;
      return <IconComponent size={18} />;
    }
    return <Bell size={18} />;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="notification-bell-wrapper" ref={dropdownRef}>
      <button
        className="notification-bell-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <div className="notification-header-actions">
              {unreadCount > 0 && (
                <button
                  className="mark-all-read-btn"
                  onClick={markAllAsRead}
                  aria-label="Mark all as read"
                >
                  Mark all read
                </button>
              )}
              <button
                className="close-dropdown"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <Bell size={32} />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification)}
                  </div>
                  <div className="notification-content">
                    <h4>{notification.title}</h4>
                    {notification.content?.headline && (
                      <p>{notification.content.headline}</p>
                    )}
                    <span className="notification-time">
                      {formatDate(notification.created_at)}
                    </span>
                  </div>
                  {!notification.is_read && <div className="unread-dot" />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
